import {
  Calendar,
  ChevronDown,
  Clock,
  ExternalLink,
  Eye,
  Search,
  User,
  Youtube,
} from 'lucide-react'
import { useState } from 'react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import type { YoutubeSearchResult } from 'sprites-types/workflow/tools/youtubeSearch'
import { youtubeSearch } from 'sprites-types/workflow/tools/youtubeSearch'
import { ContextMenuWrapper } from '../shared/ContextMenuWrapper'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function YouTubeVideoItem({ video, className }: { video: any; className?: string }) {
  const title = video.title || 'Untitled Video'
  const channel = video.channelTitle || video.channel || video.channelName || 'Unknown Channel'
  const description = video.description || video.snippet || ''
  const viewCount = video.viewCount || video.views || 0
  const duration = video.duration || video.lengthText || ''
  const publishedAt = video.publishedAt || video.publishTime || video.publishDate
  const videoUrl =
    video.url ||
    video.videoUrl ||
    video.link ||
    `https://youtube.com/watch?v=${video.videoId || video.id}`
  const thumbnail =
    video.thumbnail ||
    video.thumbnailUrl ||
    video.thumbnails?.medium?.url ||
    video.thumbnails?.high?.url

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
    return num.toLocaleString()
  }

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString()
    } catch {
      return dateStr
    }
  }

  return (
    <div
      className={`rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-sm ${className}`}
    >
      <div className="px-4 py-3">
        <div className="flex items-start gap-3">
          {thumbnail && (
            <div className="flex-shrink-0">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={thumbnail}
                alt={title}
                className="h-20 w-32 rounded-2xl bg-gray-100 object-cover"
              />
            </div>
          )}

          <div className="min-w-0 flex-1">
            <div className="mb-1 line-clamp-2 text-sm font-medium text-gray-900">{title}</div>

            <div className="mb-2 flex items-center gap-1 text-xs text-gray-600">
              <User className="h-3 w-3" />
              <span>{channel}</span>
            </div>

            {description && (
              <div className="mb-2 line-clamp-2 text-xs text-gray-700">
                {description.length > 120 ? `${description.slice(0, 120)}...` : description}
              </div>
            )}

            <div className="mb-2 flex items-center gap-4 text-xs text-gray-600">
              {viewCount > 0 && (
                <div className="flex items-center gap-1">
                  <Eye className="h-3 w-3" />
                  <span>{formatNumber(viewCount)} views</span>
                </div>
              )}
              {duration && (
                <div className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  <span>{duration}</span>
                </div>
              )}
              {publishedAt && (
                <div className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  <span>{formatDate(publishedAt)}</span>
                </div>
              )}
            </div>

            <div className="flex items-center gap-3">
              <a
                href={videoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-xs text-red-600 hover:text-red-800"
              >
                <ExternalLink className="h-3 w-3" />
                Watch Video
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function YoutubeSearchDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const [isExpanded, setIsExpanded] = useState(true)

  const schema = getToolResultSchema(youtubeSearch, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const parsed = parseResult.data
  const inputs = nodeExecution.inputs

  const query = inputs.query || inputs.search || inputs.q || 'search'
  const normalized: YoutubeSearchResult[] = Array.isArray(parsed) ? parsed : [parsed]
  const videos: any[] = normalized.flatMap((r) => r.results)
  const totalViews = videos.reduce(
    (sum, v) => sum + (typeof v.viewCount === 'number' ? v.viewCount : 0),
    0
  )

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
    return num.toLocaleString()
  }

  if (videos.length === 0) {
    return (
      <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
        <div className="flex items-center gap-4 px-4 py-3">
          <div className="flex-shrink-0">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-gray-900">No videos found</div>
            <div className="text-xs text-gray-600">Search: &ldquo;{query}&rdquo;</div>
          </div>
        </div>
      </div>
    )
  }

  if (videos.length === 1) {
    return <YouTubeVideoItem video={videos[0]} />
  }

  return (
    <div className="space-y-3">
      <ContextMenuWrapper actions={[]}>
        <div className="overflow-hidden rounded-3xl border border-gray-200/50 bg-gray-200/70 text-black shadow-sm">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="group flex w-full items-center gap-4 bg-white px-4 py-3 text-left transition-colors hover:bg-gray-50/50"
            aria-expanded={isExpanded}
          >
            <div className="flex-shrink-0">
              <Youtube className="h-5 w-5 text-red-600" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-gray-900">
                YouTube Search Results
              </div>
              <div className="text-xs font-medium tracking-wide text-gray-500">
                {videos.length} videos • &ldquo;{query}&rdquo; • {formatNumber(totalViews)} total
                views
              </div>
            </div>
            <div className="flex-shrink-0">
              <ChevronDown
                className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </div>
          </button>
        </div>
      </ContextMenuWrapper>

      {isExpanded && (
        <div className="space-y-2">
          {videos.map((video: any, index: number) => (
            <YouTubeVideoItem
              key={video.videoId || video.id || video.url || index}
              video={video}
              className="mx-8"
            />
          ))}
        </div>
      )}
    </div>
  )
}

function downloadYoutubeSearch(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function youtubeSearchToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(youtubeSearch, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data
  const inputs = nodeExecution.inputs

  const query = inputs.query || inputs.search || inputs.q || 'search'
  const videos = Array.isArray(result)
    ? result
    : (result as any)?.data ||
      (result as any)?.videos ||
      (result as any)?.items ||
      (result ? [result] : [])

  if (videos.length === 0) {
    return `## YouTube Search Results

**Query:** "${query}"

No videos found.

---
`
  }

  const tableHeader = `| Title | Channel | Views | Duration | Published | Link |
|-------|---------|-------|----------|-----------|------|`

  const tableRows = videos
    .map((video: any) => {
      const title = (video.title || 'Untitled Video')
        .replace(/\|/g, '\\|')
        .replace(/\n/g, ' ')
        .slice(0, 40)
      const titleDisplay = title.length === 40 ? title + '...' : title
      const channel = video.channelTitle || video.channel || video.channelName || 'Unknown'
      const viewCount = video.viewCount || video.views || 0
      const viewsDisplay = viewCount > 0 ? viewCount.toLocaleString() : '-'
      const duration = video.duration || video.lengthText || '-'
      const publishedAt = video.publishedAt || video.publishTime || video.publishDate
      const publishedDisplay = publishedAt ? new Date(publishedAt).toLocaleDateString() : '-'
      const videoUrl =
        video.url ||
        video.videoUrl ||
        video.link ||
        `https://youtube.com/watch?v=${video.videoId || video.id}`
      const linkDisplay = `[Watch](${videoUrl})`

      return `| ${titleDisplay} | ${channel} | ${viewsDisplay} | ${duration} | ${publishedDisplay} | ${linkDisplay} |`
    })
    .join('\n')

  const detailedDescriptions = videos
    .filter((video: any) => (video.description || video.snippet)?.length > 100)
    .slice(0, 5) // Limit to first 5 detailed descriptions
    .map((video: any) => {
      const title = video.title || 'Untitled Video'
      const description = video.description || video.snippet || ''
      return `### ${title}\n\n${description.slice(0, 300)}${description.length > 300 ? '...' : ''}`
    })
    .join('\n\n')

  return `## YouTube Search Results (${videos.length} videos)

**Query:** "${query}"

${tableHeader}
${tableRows}

${detailedDescriptions ? `\n## Video Descriptions\n\n${detailedDescriptions}\n` : ''}
---
`
}

export const YoutubeSearchDisplay = createWorkflowToolComponent(
  YoutubeSearchDisplayComponent,
  downloadYoutubeSearch,
  youtubeSearchToMarkdown
)
